import type { EventConfig } from '@/types'
import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, Routes, Route, Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { ArrowLeft, Download, CheckCircle2, Camera, Monitor, Printer, SlidersHorizontal, Share2, ClipboardList, Pencil, Check, X, ChevronLeft, ChevronRight, Smartphone, Play, AlertCircle } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useEventStore, useAppStore } from '@/store'
import { useI18n } from '@/lib/i18n'
import { exportEvent } from '@/lib/zip'
import { cn } from '@/lib/utils'
import EventOverview from './EventOverview'
import EventSettingsGroup from '../components/EventSettingsGroup'
import { GROUP_SECTIONS, type EventGroup } from '@/lib/settingsPresentation'
import { isInherited } from '../../../server/lib/eventSettings'

const getTabs = (t: (key: string) => string) => [
  { path: 'overview', label: t('inheritance.nav.overview'), icon: ClipboardList, group: 'setup' },
  { path: 'capture', label: t('inheritance.nav.capture'), icon: Camera, group: 'setup' },
  { path: 'audio-video', label: t('inheritance.nav.audio-video'), icon: SlidersHorizontal, group: 'setup' },
  { path: 'design', label: t('inheritance.nav.design'), icon: Printer, group: 'experience' },
  { path: 'display', label: t('inheritance.nav.display'), icon: Monitor, group: 'experience' },
  { path: 'guest', label: t('inheritance.nav.guest'), icon: Share2, group: 'experience' },
  { path: 'experience', label: t('inheritance.nav.experience'), icon: Play, group: 'experience' },
  { path: 'advanced', label: t('inheritance.nav.advanced'), icon: Smartphone, group: 'advanced' },
]
const groups = ['setup', 'experience', 'advanced'] as const
const aliases: Record<string, string> = {
  camera: 'audio-video#camera', audio: 'audio-video#audio', screensaver: 'display#screensaver', kiosk: 'display#slideshow',
  screens: 'design/screens', print: 'design/print', branding: 'design/branding', poses: 'capture/poses',
  sharing: 'guest/sharing', 'gallery-settings': 'guest/gallery', effects: 'experience/effects', survey: 'experience/survey',
  attendant: 'experience/attendant', triggers: 'advanced/triggers', remote: 'advanced/remote',
}

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error'

function InlineRename({ name, onSave }: { name: string; onSave: (n: string) => void }) {
  const [editing, setEditing] = useState(false)
  const [val, setVal] = useState(name)
  const inputRef = useRef<HTMLInputElement>(null)

  const commit = () => {
    const trimmed = val.trim()
    if (trimmed && trimmed !== name) onSave(trimmed)
    setEditing(false)
  }

  const cancel = () => {
    setVal(name)
    setEditing(false)
  }

  useEffect(() => {
    if (editing) inputRef.current?.focus()
  }, [editing])

  if (editing) {
    return (
      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        <input
          ref={inputRef}
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') cancel() }}
          className="rounded border border-primary bg-background px-2 py-0.5 text-sm font-semibold text-foreground focus:outline-none w-64"
        />
        <button onClick={commit} className="rounded p-0.5 hover:bg-green-500/20 text-green-400"><Check className="h-3.5 w-3.5" /></button>
        <button onClick={cancel} className="rounded p-0.5 hover:bg-destructive/20 text-muted-foreground"><X className="h-3.5 w-3.5" /></button>
      </div>
    )
  }

  return (
    <div 
      className="flex items-center gap-1.5 group/rename cursor-pointer"
      onClick={() => setEditing(true)}
    >
      <h1 className="text-sm font-semibold leading-tight group-hover/rename:text-primary transition-colors">{name}</h1>
      <Pencil className="h-3 w-3 opacity-0 group-hover/rename:opacity-100 text-muted-foreground transition-opacity" />
    </div>
  )
}

function SlugRename({ eventId, currentTab }: { eventId: string; currentTab: string }) {
  const [editing, setEditing] = useState(false)
  const [val, setVal] = useState(eventId)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()
  const renameEventId = useEventStore((s) => s.renameEventId)

  useEffect(() => {
    if (editing) {
      setVal(eventId)
      setError(null)
      inputRef.current?.focus()
      inputRef.current?.select()
    }
  }, [editing, eventId])

  const commit = async () => {
    const trimmed = val.trim().toLowerCase()
    if (!trimmed || trimmed === eventId) { setEditing(false); return }

    // Client-side slug validation before hitting the server
    if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(trimmed)) {
      setError('Only lowercase letters, numbers, and hyphens allowed (no leading/trailing hyphens)')
      return
    }

    setSaving(true)
    setError(null)
    try {
      const res = await fetch(`/api/events/${eventId}/rename-slug`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newSlug: trimmed }),
      })
      const data = await res.json()
      if (!res.ok) {
        setError(data.error || 'Failed to rename slug')
        setSaving(false)
        return
      }
      // Update local Zustand state
      renameEventId(eventId, data.id)
      setEditing(false)
      // Navigate to the same tab under the new event id
      navigate(`/admin/events/${data.id}/${currentTab}`, { replace: true })
    } catch {
      setError('Network error, please try again')
      setSaving(false)
    }
  }

  const cancel = () => {
    setVal(eventId)
    setError(null)
    setEditing(false)
  }

  if (editing) {
    return (
      <div className="flex flex-col gap-1" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-muted-foreground opacity-60">/booth/</span>
          <input
            ref={inputRef}
            value={val}
            onChange={(e) => { setVal(e.target.value); setError(null) }}
            onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') cancel() }}
            disabled={saving}
            className="rounded border border-primary bg-background px-2 py-0.5 text-xs font-mono text-foreground focus:outline-none w-52 disabled:opacity-50"
          />
          <button onClick={commit} disabled={saving} className="rounded p-0.5 hover:bg-green-500/20 text-green-400 disabled:opacity-50">
            {saving ? <div className="h-3.5 w-3.5 animate-spin rounded-full border border-green-400 border-t-transparent" /> : <Check className="h-3.5 w-3.5" />}
          </button>
          <button onClick={cancel} disabled={saving} className="rounded p-0.5 hover:bg-destructive/20 text-muted-foreground"><X className="h-3.5 w-3.5" /></button>
        </div>
        {error && (
          <div className="flex items-center gap-1 text-[10px] text-destructive">
            <AlertCircle className="h-2.5 w-2.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}
      </div>
    )
  }

  return (
    <div
      className="flex items-center gap-1 group/slug cursor-pointer"
      onClick={() => setEditing(true)}
      title="Click to rename the URL slug"
    >
      <span className="opacity-60 text-[11px] text-muted-foreground">/booth/</span>
      <span className="font-mono text-[11px] text-primary/70 group-hover/slug:text-primary transition-colors">{eventId}</span>
      <Pencil className="h-2.5 w-2.5 opacity-0 group-hover/slug:opacity-100 text-muted-foreground transition-opacity" />
    </div>
  )
}

export default function EventEditor() {
  const { eventId } = useParams<{ eventId: string }>()
  const { t } = useI18n()
  const events = useEventStore((s) => s.events)
  const loadEvents = useEventStore((s) => s.loadEvents)
  const saveEvent = useEventStore((s) => s.saveEvent)
  const event = events.find((e) => e.id === eventId)
  const storedEvent = useEventStore(s => eventId ? s.storedEvents[eventId] : undefined)
  const defaults = useEventStore(s => s.defaults)
  const [loadError, setLoadError] = useState('')
  const [saveError, setSaveError] = useState('')
  const pendingSave = useRef<EventConfig | null>(null)
  const saveQueue = useRef<Promise<void>>(Promise.resolve())
  const editSequence = useRef(0)
  const [loading, setLoading] = useState(events.length === 0)
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle')
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false)
  // Zipping an event with a few large backgrounds takes a noticeable moment
  // with no browser feedback until the download appears, so say what's going on.
  const [exportStatus, setExportStatus] = useState<'idle' | 'preparing' | 'done' | 'error'>('idle')
  const location = useLocation()
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const setOuterSidebarOpen = useAppStore((s) => s.setSidebarOpen)

  // Derive the active sub-tab from the URL so SlugRename can preserve it after a rename.
  // e.g. "/admin/events/my-event/gallery-settings" → "gallery-settings"
  const currentTab = (() => {
    const prefix = `/admin/events/${eventId}/`
    const idx = location.pathname.indexOf(prefix)
    if (idx === -1) return 'overview'
    const rest = location.pathname.slice(idx + prefix.length)
    return rest || 'overview'
  })()

  // Entering an event's editor: collapse the outer Events/Settings nav (it's
  // not useful while deep in a specific event) so the event's own tab list, // Setup/Production/Guest/Advanced, has the room and is what's visible.
  useEffect(() => {
    setOuterSidebarOpen(false)
  }, [setOuterSidebarOpen])

  useEffect(() => {
    if (events.length === 0) {
      loadEvents().catch(error => setLoadError(error.message)).finally(() => setLoading(false))
    }
  }, [events.length, loadEvents])

  const persist = useCallback((snapshot: EventConfig) => {
    const sequence = editSequence.current
    saveQueue.current = saveQueue.current.catch(() => {}).then(() => saveEvent(snapshot))
    saveQueue.current.then(() => {
      if (sequence === editSequence.current) { setSaveStatus('saved'); setSaveError('') }
    }).catch(error => {
      if (sequence === editSequence.current) { setSaveStatus('error'); setSaveError(error.message) }
    })
  }, [saveEvent])

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (pendingSave.current) {
      persist(pendingSave.current)
      pendingSave.current = null
    }
  }, [eventId, persist])

  const update = useCallback((updates: Partial<EventConfig>) => {
    if (!eventId) return
    useEventStore.getState().updateEvent(eventId, updates)
    const snapshot = useEventStore.getState().storedEvents[eventId]
    if (!snapshot) return
    editSequence.current++
    pendingSave.current = snapshot
    setSaveStatus('saving'); setSaveError('')
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      const latest = pendingSave.current
      pendingSave.current = null
      if (latest) persist(latest)
    }, 500)
  }, [eventId, persist])

  const handleExport = async () => {
    if (!event || exportStatus === 'preparing') return
    setExportStatus('preparing')
    try {
      await exportEvent(event, async (url) => {
        try {
          const res = await fetch(url)
          return res.ok ? res.blob() : null
        } catch { return null }
      })
      setExportStatus('done')
      setTimeout(() => setExportStatus('idle'), 2500)
    } catch (err) {
      console.error('Export failed:', err)
      setExportStatus('error')
      setTimeout(() => setExportStatus('idle'), 5000)
    }
  }

  if (loadError) return <div role="alert" className="p-6 text-destructive">{loadError}<button className="ml-3 underline" onClick={() => window.location.reload()}>{t('inheritance.reload')}</button></div>

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="flex items-center gap-3 text-muted-foreground">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-border border-t-primary" />
          {t('editor.loading')}
        </div>
      </div>
    )
  }

  if (!event || !storedEvent) {
    return <div className="flex h-full items-center justify-center text-muted-foreground">{t('editor.notFound')}</div>
  }


  return (
    <div className="flex h-full flex-col">
      {/* Export toast, top right. The zip is built in the page, so there's no
          browser download indicator until it's finished. */}
      <AnimatePresence>
        {exportStatus !== 'idle' && (
          <motion.div
            initial={{ opacity: 0, y: -12, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -12, scale: 0.97 }}
            className="fixed right-5 top-5 z-50 w-64 rounded-xl border border-border bg-card/95 p-3.5 shadow-md"
          >
            <div className="flex items-center gap-2">
              {exportStatus === 'preparing' && <Download className="h-4 w-4 shrink-0 text-primary" />}
              {exportStatus === 'done' && <CheckCircle2 className="h-4 w-4 shrink-0 text-green-400" />}
              {exportStatus === 'error' && <X className="h-4 w-4 shrink-0 text-destructive" />}
              <span className="text-sm font-semibold">
                {exportStatus === 'preparing' && 'Preparing your file…'}
                {exportStatus === 'done' && 'Download started'}
                {exportStatus === 'error' && 'Export failed'}
              </span>
            </div>

            <p className="mt-1 pl-6 text-xs text-muted-foreground">
              {exportStatus === 'preparing' && 'Bundling settings and images'}
              {exportStatus === 'done' && 'Check your downloads folder'}
              {exportStatus === 'error' && 'See the console for details'}
            </p>

            {/* Indeterminate bar: the total size isn't known up front, so this
                shows activity rather than a fake percentage. */}
            {exportStatus === 'preparing' && (
              <div className="relative mt-2.5 h-1 w-full overflow-hidden rounded-full bg-muted">
                <motion.div
                  className="absolute inset-y-0 w-1/3 rounded-full bg-primary"
                  animate={{ left: ['-33%', '100%'] }}
                  transition={{ duration: 1.1, repeat: Infinity, ease: 'easeInOut', repeatType: 'reverse' }}
                />
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Top bar */}
      <div className="flex h-14 items-center justify-between border-b border-border px-5 shrink-0">
        <div className="flex items-center gap-3">
          <Link to="/admin" className="rounded-md p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div>
            <InlineRename name={event.name} onSave={(name) => update({ name })} />
            <div className="text-[11px] text-muted-foreground flex items-center gap-1.5">
              <span className="opacity-60">URL:</span>
              <SlugRename eventId={event.id} currentTab={currentTab} />
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {saveStatus === 'error' && <button type="button" role="alert" onClick={() => update({})} className="text-xs text-destructive underline" title={saveError}>{t('inheritance.saveError')} · {t('inheritance.retry')}</button>}
          {/* Save status */}
          <div className="flex items-center gap-1.5 text-xs transition-all">
            {saveStatus === 'saving' && (
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <div className="h-3 w-3 animate-spin rounded-full border border-border border-t-muted-foreground" />
                {t('editor.saving')}
              </span>
            )}
            {saveStatus === 'saved' && (
              <span className="flex items-center gap-1.5 text-green-400">
                <CheckCircle2 className="h-3.5 w-3.5" />
                {t('editor.saved')}
              </span>
            )}
          </div>
          <a
            href={`/booth/${event.id}`}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1.5 rounded-lg bg-emerald-500/15 border border-emerald-500/20 px-3 py-1.5 text-xs font-semibold text-emerald-400 hover:bg-emerald-500/25 transition-colors"
          >
            {t('editor.launchBooth')}
          </a>
          <button
            onClick={handleExport}
            disabled={exportStatus === 'preparing'}
            className="flex items-center gap-1.5 rounded-lg bg-secondary px-3 py-1.5 text-xs font-medium hover:bg-secondary/80 disabled:opacity-60 transition-colors"
          >
            {exportStatus === 'preparing' ? (
              <div className="h-3.5 w-3.5 animate-spin rounded-full border border-muted-foreground border-t-transparent" />
            ) : (
              <Download className="h-3.5 w-3.5" />
            )}
            {t('editor.exportEvent')}
          </button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Left nav */}
        <div className={cn(
          "border-r border-border bg-card/50 overflow-auto shrink-0 py-3 px-2 space-y-4 transition-all duration-300 relative",
          isSidebarCollapsed ? "w-12" : "w-52"
        )}>
          <button 
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            className="w-full flex items-center justify-center p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors mb-2"
            title={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isSidebarCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
          
          {groups.map((group) => {
            const groupTabs = getTabs(t).filter((tab) => tab.group === group)

            return (
              <div key={group}>
                {!isSidebarCollapsed && (
                  <div className="mb-1.5 px-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/60">{t(`inheritance.sidebar.${group}`)}</div>
                )}
                {groupTabs.map((tab) => {
                  const active = location.pathname.includes(`/admin/events/${eventId}/${tab.path}`)

                  return (
                    <Link
                      key={tab.path}
                      to={`/admin/events/${eventId}/${tab.path}`}
                      className={cn(
                        'flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                        active
                          ? 'bg-primary/15 text-primary border border-primary/20'
                          : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                        isSidebarCollapsed && 'justify-center px-0'
                      )}
                      title={isSidebarCollapsed ? tab.label : undefined}
                    >
                      <tab.icon className="h-4 w-4 shrink-0" />
                      {!isSidebarCollapsed && <span className="min-w-0"><span className="block">{tab.label}</span>{GROUP_SECTIONS[tab.path as EventGroup] && <span className="block text-[10px] font-normal opacity-70">{t('inheritance.counts', { inherited: GROUP_SECTIONS[tab.path as EventGroup]!.filter(key => isInherited(storedEvent, key)).length, custom: GROUP_SECTIONS[tab.path as EventGroup]!.filter(key => !isInherited(storedEvent, key)).length })}</span>}</span>}
                    </Link>
                  )
                })}
              </div>
            )
          })}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          <Routes>
            <Route path="overview" element={<><div className="mb-6 flex flex-wrap gap-3">{(['audio-video', 'display', 'capture'] as EventGroup[]).map(group => <Link key={group} to={`/admin/events/${event.id}/${group}`} className="rounded-xl border border-border bg-card p-4 text-sm"><span className="block font-semibold">{t(`inheritance.nav.${group}`)}</span><span className="mt-1 block text-xs text-muted-foreground">{t('inheritance.counts', { inherited: GROUP_SECTIONS[group]!.filter(key => isInherited(storedEvent, key)).length, custom: GROUP_SECTIONS[group]!.filter(key => !isInherited(storedEvent, key)).length })}</span></Link>)}</div><EventOverview event={event} onChange={update} /></>} />
            {(['capture', 'audio-video', 'design', 'display', 'guest', 'experience', 'advanced'] as EventGroup[]).map(group => <Route key={group} path={`${group}/*`} element={<EventSettingsGroup key={group} group={group} event={event} storedEvent={storedEvent} defaults={defaults} onChange={update} />} />)}
            {Object.entries(aliases).map(([path, destination]) => <Route key={path} path={path} element={<Navigate to={`/admin/events/${event.id}/${destination}`} replace />} />)}
            <Route path="*" element={<Navigate to={`/admin/events/${event.id}/overview`} replace />} />
          </Routes>
        </div>
      </div>
    </div>
  )
}
